// Grow every <textarea> in the app to fit its content.
// The `rows` attribute stays the minimum height; add `data-no-autosize` to a textarea to opt out.

const OPT_OUT = 'data-no-autosize'
const lastWidth = new WeakMap()
const queue = new Set()
let frame = null

function isAutosized(el) {
  return el instanceof HTMLTextAreaElement && el.isConnected && !el.hasAttribute(OPT_OUT)
}

function scrollParents(el) {
  const list = []
  let node = el.parentNode
  while (node && node instanceof Element) {
    if (node.scrollTop) list.push([node, node.scrollTop])
    node = node.parentNode
  }
  const root = document.scrollingElement || document.documentElement
  list.push([root, root.scrollTop])
  return list
}

function resize(el) {
  if (!isAutosized(el)) return
  // hidden (inactive tab, closed modal): ResizeObserver resizes it once it is shown
  if (!el.getClientRects().length) return

  const style = window.getComputedStyle(el)
  const extra = style.boxSizing === 'border-box'
    ? parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
    : -(parseFloat(style.paddingTop) + parseFloat(style.paddingBottom))

  // collapsing to 'auto' briefly shrinks the page, so keep scroll positions from jumping
  const scrolls = scrollParents(el)
  el.style.overflowY = 'hidden'
  el.style.height = 'auto'
  el.style.height = (el.scrollHeight + extra) + 'px'
  scrolls.forEach(([node, top]) => { if (node.scrollTop !== top) node.scrollTop = top })
}

function schedule(el) {
  queue.add(el)
  if (frame) return
  frame = window.requestAnimationFrame(() => {
    frame = null
    const items = Array.from(queue)
    queue.clear()
    items.forEach(resize)
  })
}

// fires on first observe, when a hidden textarea becomes visible, and when its width changes
const resizeObserver = new ResizeObserver(entries => {
  entries.forEach(({ target, contentRect }) => {
    if (lastWidth.get(target) === contentRect.width) return
    lastWidth.set(target, contentRect.width)
    schedule(target)
  })
})

function eachTextarea(node, fn) {
  if (!(node instanceof Element)) return
  if (node instanceof HTMLTextAreaElement) fn(node)
  else node.querySelectorAll('textarea').forEach(fn)
}

// v-model / b-form-textarea set `value` programmatically, which fires no input event
const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')
Object.defineProperty(HTMLTextAreaElement.prototype, 'value', {
  configurable: true,
  enumerable: valueDescriptor.enumerable,
  get() { return valueDescriptor.get.call(this) },
  set(v) {
    valueDescriptor.set.call(this, v)
    if (isAutosized(this)) schedule(this)
  },
})

function start() {
  document.addEventListener('input', e => {
    if (isAutosized(e.target)) resize(e.target)
  }, true)

  new MutationObserver(mutations => {
    mutations.forEach(m => {
      m.addedNodes.forEach(n => eachTextarea(n, el => resizeObserver.observe(el)))
      m.removedNodes.forEach(n => eachTextarea(n, el => resizeObserver.unobserve(el)))
    })
  }).observe(document.body, { childList: true, subtree: true })

  document.querySelectorAll('textarea').forEach(el => resizeObserver.observe(el))
}

if (document.body) start()
else document.addEventListener('DOMContentLoaded', start)
